import Icon from 'component/ui/Icon';
import type * as CSS from 'csstype';
import { Design, IconName, Intent } from 'enums';
import { TestSelector } from 'enums/tests';
import { ThemeColors } from 'providers/Theme';
import * as React from 'react';

import { AriaHidden, DefaultButtonIcon } from './styles';

export interface IButtonIcon {
  block?: boolean;
  color?: string;
  intent?: Intent;
  /*
    Selector name for e2e tests
  */
  iconDataTest?: TestSelector;
  iconName: IconName;
  iconHeight?: CSS.Property.Height;
  iconWidth?: CSS.Property.Width;
  iconThemeColor?: ThemeColors;
  iconMinWidth?: CSS.Property.MinWidth;
  minimal?: boolean;
  design?: Design;
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => any;
  height?: string;
  width?: string;
  minWidth?: string;
  rotate?: boolean;
  disabled?: boolean;
  // TODO make title required prop
  title?: string;
  style?: Record<string, unknown>;
  border?: string;
  borderRadius?: string;
  ariaLiveMessage?: string;
  type?: 'button' | 'submit' | 'reset';
  margin?: CSS.Property.Margin;
  padding?: CSS.Property.Padding;
}

/**
 *
 * @param props
 *  @param props.block - Determines if the button should render full width
 * @param props.color - Color of the icon
 * @param props.intent - The Intent of the button (Primary, warning, success etc)
 * @param props.iconName - The name of the icon to be rendered
 * @param props.minimal -
 * @param props.design - The Design of the button
 * @param props.onClick - Click event fired when the icon is clicked
 * @param props.height -
 * @param props.width -
 * @param props.rotate -
 * @returns
 */
const ButtonIcon = ({
  color,
  design = Design.STANDARD,
  disabled,
  height,
  iconDataTest,
  iconName = IconName.PENCIL_WRITE,
  iconHeight,
  iconWidth,
  iconThemeColor,
  iconMinWidth,
  onClick,
  rotate,
  width,
  border,
  borderRadius,
  title,
  ariaLiveMessage,
  type = 'button',
  margin,
  padding,
  ...props
}: IButtonIcon) => {
  return (
    <>
      <DefaultButtonIcon
        aria-label={title}
        $design={design}
        $height={height}
        $width={width}
        $border={border}
        $borderRadius={borderRadius}
        $margin={margin}
        {...props}
        onClick={onClick}
        $disabled={disabled}
        type={type}
        $padding={padding}
      >
        <Icon
          rotate={rotate}
          name={iconName}
          color={color}
          dataTest={iconDataTest}
          themeColor={iconThemeColor}
          height={iconHeight}
          width={iconWidth}
          minWidth={iconMinWidth}
        />
      </DefaultButtonIcon>
      {ariaLiveMessage && (
        <AriaHidden aria-live="polite">{ariaLiveMessage}</AriaHidden>
      )}
    </>
  );
};

export default ButtonIcon;
